import React, { Fragment } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import MaldivesDirectSearchFlightCalendarItem from './MaldivesDirectSearchFlightCalendarItem'
import MaldivesOfferCalendarItem from './MaldivesOfferCalendarItem'
import { CHARTER_CALENDAR_PAGE_SIZE } from 'constants/charterFlight'
import { FlightsClassTypes } from 'constants/flight'
import { CalendarItemBundleData } from '../hooks/types'
import Hr from 'components/Common/Hr'

interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
}

export interface DateItem {
  id: string
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
  month: string
}

interface Props {
  currentMonthTripOptions: Array<DateItem>
  selectedMonth: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  luxPlusEnabled: boolean
  selectedOptionId?: string
  onOptionSelect: (option: TripOption) => void
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  mode: 'search' | 'offer'
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
}

const OptionsGrid = styled.div`
  display: grid;
  align-items: center;
  gap: ${rem(20)} ${rem(12)};
  grid-template-columns: auto auto auto 1fr auto;
`

const DepartureHeader = styled(BodyText)`
  grid-column: 1;
`

const ReturnHeader = styled(BodyText)`
  grid-column: 3;
`

const PriceHeader = styled(BodyText)`
  grid-column: 5;
`

const RowDivider = styled(Hr)`
  grid-column: 1 / -1;
`

const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

const ShowMoreButtonContainer = styled.div`
  padding: ${rem(20)};
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  grid-column: 1 / -1;
`

const pageSize = CHARTER_CALENDAR_PAGE_SIZE

const TripOptionsList = React.memo(function TripOptionsList(props: Props) {
  const {
    currentMonthTripOptions,
    selectedMonth,
    dateRangesByMonth,
    luxPlusEnabled,
    selectedOptionId,
    onOptionSelect,
    nights,
    rooms,
    fareClass,
    priceMode,
    mode,
    itemBundleDataByDate,
  } = props

  const [page, setPage] = React.useState<number>(1)

  // Get the first date of the selected month to determine the year
  const selectedMonthDateRanges = dateRangesByMonth[selectedMonth]
  const selectedMonthFirstDate = selectedMonthDateRanges?.[0]?.checkIn
  const selectedMonthMoment = selectedMonthFirstDate
    ? moment(selectedMonthFirstDate).startOf('month')
    : null

  // Filter visible items and calculate limit
  const visibleItems = React.useMemo(() => {
    return currentMonthTripOptions.filter((item) => {
      if (!selectedMonthMoment) return false
      const itemMonthMoment = moment(item.checkIn).startOf('month')
      return itemMonthMoment.isSameOrAfter(selectedMonthMoment, 'month')
    })
  }, [currentMonthTripOptions, selectedMonthMoment])

  const limit = page * pageSize
  const itemsToShow = visibleItems.slice(0, limit)
  const hasMore = visibleItems.length > limit

  // Reset page when selectedMonth changes
  React.useEffect(() => {
    setPage(1)
  }, [selectedMonth])

  const onShowMore = React.useCallback(() => {
    setPage(prevPage => prevPage + 1)
  }, [])

  // Early return after all hooks
  if (currentMonthTripOptions.length === 0) {
    return (
      <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
        <Heading variant="heading5" align="center">
          No departures available for {selectedMonth}
        </Heading>
        <BodyText variant="medium" align="center" colour="neutral-three">
          Try selecting a different month or adjusting your search criteria
        </BodyText>
      </EmptyStateGroup>
    )
  }

  return (
    <OptionsGrid>
      <DepartureHeader variant="small" colour="neutral-three">Departs Melbourne</DepartureHeader>
      <ReturnHeader variant="small" colour="neutral-three">Departs Malé</ReturnHeader>
      <PriceHeader variant="small" colour="neutral-three">Flight + Hotel price</PriceHeader>

      <RowDivider />

      {itemsToShow.map((item) => {
        const isSelected = item.id === selectedOptionId
        const commonProps = {
          checkIn: item.checkIn,
          checkOut: item.checkOut,
          departDate: item.departDate,
          returnDate: item.returnDate,
          nights: nights,
          priceMode: priceMode,
          luxPlusEnabled: luxPlusEnabled,
          isSelected: isSelected,
          onSelect: onOptionSelect,
        }

        switch (mode) {
          case 'search':
            return (
              <Fragment key={item.id}>
                <MaldivesDirectSearchFlightCalendarItem
                  {...commonProps}
                  rooms={rooms}
                  fareClass={fareClass}
                  prefetchedBundleData={itemBundleDataByDate?.[item.checkIn]}
                />
                <RowDivider />
              </Fragment>
            )
          case 'offer': {
            // Get pre-calculated bundle data for this date
            const itemBundleData = itemBundleDataByDate?.[item.checkIn]

            // Fallback to default empty state if data is missing
            const itemData: CalendarItemBundleData = itemBundleData ?? {
              fetching: true,
              available: false,
              totalPrice: 0,
              pricePerPerson: 0,
              offerCount: 0,
              seatsLeft: 0,
              hasNoPackage: false,
            }

            return (
              <Fragment key={item.id}>
                <MaldivesOfferCalendarItem
                  {...commonProps}
                  bundleData={itemData}
                  rooms={rooms}
                />
                <RowDivider />
              </Fragment>
            )
          }
          default:
            return null
        }
      })}
      {hasMore && <ShowMoreButtonContainer>
        <TextButton
          variant="dark"
          kind="secondary"
          onClick={onShowMore}
        >
          Show more dates
        </TextButton>
      </ShowMoreButtonContainer>}
    </OptionsGrid>
  )
})

export default TripOptionsList
